import {customElement} from "lit/decorators/custom-element.js";
import {AbstractHaTimelineView} from "./abstract-ha-timeline.view";
import {css, html} from "lit";
import {appState} from "../app.state";
import {container} from "tsyringe";
import {TimelineConfigurationManager} from "../services/timeline-configuration.manager";
import {TimelineConfiguration} from "../timeline/timeline-configuration.interface";
import {takeUntil} from "rxjs";
import {TimelineRowHeader} from "../timeline/timeline-row-header.interface";
import {repeat} from "lit/directives/repeat.js";
import {Entity} from "../domain/entities/entity";
import {classMap} from "lit/directives/class-map.js";
import {styleMap} from "lit/directives/style-map.js";
import type {PopoverListItem} from "@o2d/popover-list";
import {get} from "lit-translate";
import {StringHelper} from "../helpers/string.helper";
import {ArrayHelper} from "../helpers/array.helper";
import {legacyRowHeaderColorStyle} from "./_shared/legacy-grid-color-style";

@customElement(`ha-user-view`)
export class HaUserView extends AbstractHaTimelineView {

    static styles = css`
        :host {
            flex: 1;
        }
    `;

    private readonly _timelineConfigurationManager = container.resolve(TimelineConfigurationManager);

    protected get _tags(): Array<string[]> {
        return [];
    }

    public async connectedCallback(): Promise<void> {

        appState.listen$(`selectedTimelineEntities`).pipe(
            takeUntil(this._disconnected$)
        ).subscribe(() => this._buildTimelineConfigurationFromUserViewConfigurations());

        this._buildTimelineConfigurationFromUserViewConfigurations();

        super.connectedCallback();

    }

    protected rowSortingFn: (rowHeaderA: TimelineRowHeader<any>, rowHeaderB: TimelineRowHeader<any>) => number = (rowHeaderA: TimelineRowHeader<any>, rowHeaderB: TimelineRowHeader<any>): number => {
        return rowHeaderA.title.localeCompare(rowHeaderB.title);
    };

    protected onTimelineRowHeadersUpdated(timelineRowHeaders: TimelineRowHeader<Entity>[]): TimelineRowHeader<Entity>[] {
        return ArrayHelper.unique(timelineRowHeaders, item => item.uid);
    }

    protected _renderHeaderCells(): unknown {

        return repeat(
            this._timelineRowHeadersInView,
            rowHeader => rowHeader.uid,
            (rowHeader: TimelineRowHeader<Entity>, index) => {
                const rowIndex = index + 5;
                const entityStyles = {
                    gridRowStart: rowIndex,
                    gridRowEnd: rowIndex,
                    gridColumnStart: 1,
                    gridColumnEnd: 1,
                    ...legacyRowHeaderColorStyle(rowHeader),
                };

                const entityClasses = {
                    "row__header": true,
                    "row__highlighted": this._highlightedRows.includes(rowHeader)
                }

                return html`
                    <div class=${classMap(entityClasses)}
                         style=${styleMap(entityStyles)}
                    >
                        <div class="cursor view-icon ${rowHeader.configuration.viewName}">
                            &nbsp;
                        </div>
                        <div class="row__title">
                            <div>${rowHeader.title}</div>
                        </div>
                        <div class="cursor"
                             @click=${() => this._toggleHighlightRow(rowHeader)}
                        >
                            <i class="icon icon-highlighter"></i>
                        </div>
                        <div class="cursor"
                             @click=${(evt: MouseEvent) => this._onClickRowHeaderContextMenu(rowHeader, evt)}
                        ><i class="icon icon-ellipsis-vertical"></i></div>
                    </div>
                `
            }
        );
    }

    protected _buildRowBasedContextMenu(entity: Entity<any>, link: string | null | undefined): PopoverListItem[] {
        return [
            {
                label: get(`ha-boards-view.row-title.open-link`),
                payload: entity,
                type: "item",
                id: `open-link`,
                disabled: !link
            },
            {
                label: get(`ha-boards-view.row-title.new-issue`),
                payload: entity,
                type: "item",
                id: `new-issue`,
                disabled: !this._authorizationService.hasPrivilege(`create.${StringHelper.toKebabCase(entity.constructor.name)}`)
            },
            {
                label: get(`ha-boards-view.row-title.edit`),
                payload: entity,
                type: "item",
                id: `edit`,
                disabled: !this._authorizationService.hasPrivilege(`update.${StringHelper.toKebabCase(entity.constructor.name)}`)
            }, {
                label: appState.isEntityPinnedToUserView(this, entity.uid) ? get(`ha-boards-view.row-title.unpin-to-my-view`) : get(`ha-boards-view.row-title.pin-to-my-view`),
                payload: entity,
                type: "item",
                id: appState.isEntityPinnedToUserView(this, entity.uid) ? `unpin` : `pin`
            }
        ]
    }

    private _buildTimelineConfigurationFromUserViewConfigurations(): void {
        const configurationsOfUserView = appState
            .selectedTimelineEntities
            .reduce(
                (acc: Map<string, TimelineConfiguration[]>, selectedTimelineEntity): Map<string, TimelineConfiguration[]> => {
                    if (!acc.has(selectedTimelineEntity.viewName)) {
                        const configurations = this._timelineConfigurationManager.get(selectedTimelineEntity.viewName);
                        acc.set(selectedTimelineEntity.viewName, configurations.map(configuration => ({...configuration})));
                    }
                    const configurations = acc.get(selectedTimelineEntity.viewName)!;
                    for (const configuration of configurations) {
                        if (!(configuration.rowFilterUids ?? []).includes(selectedTimelineEntity.uid)) {
                            configuration.viewName = selectedTimelineEntity.viewName;
                            configuration.rowFilterUids = [...(configuration.rowFilterUids ?? []), selectedTimelineEntity.uid];
                        }
                    }

                    return acc;
                }, new Map<string, TimelineConfiguration[]>
            );

        const configurations = Array.from(configurationsOfUserView.values()).flat();
        this.setTimelineConfiguration(...configurations);
    }
}